import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { ActKc2ListItemData, CostGroupEnum, FilterValueOfNullableOfDateTime } from 'gql/graphql';
import { ActKc2GroupTypeEnum } from 'gql/graphql';
import {
    costCellFormatter,
    costGroupFormatter,
    dateFormatter,
    getColSpan,
    GroupNameAndNumberRenderer,
} from 'pages/Project/Common/ActsKC2ListContainer';
import type { TGroupedData } from 'pages/Project/Common/ActsKC2ListContainer/helpers';

import type { CellClassParams, ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import { MenuCellRenderer } from '../helpers';
import {
    uerActKC2GroupConstructionTypeFilterGql,
    uerActKC2GroupCostGroupFilterGql,
    uerActKC2GroupDateFilterGql,
    uerActKC2GroupNumberFilterGql,
    uerActKC2GroupObjectFilterGql,
} from '../uerWorkOrderActsKc2ListQueries';

export type TFilterFieldsType = {
    fieldName: 'number' | 'actDate' | 'costGroup' | 'constructionType.name' | 'workOrderObjectName';
    checkedValues: string[];
};

const fakeComparator = () => 0;
const cellClass = (params: CellClassParams) => (params.data.isInactive ? 'inactive-act' : undefined);

export const useUerActsKc2ListColumns = ({
    workOrderId,
    groupState,
    changeGroupState,
    handleDeleteGroup,
    handlerOpenEditGroup,
    changeFilterHandler,
    activeGroupTypeId,
}: {
    workOrderId: number;
    groupState: { [key: string]: boolean };
    changeGroupState: (id: string) => () => void;
    handleDeleteGroup?: (id: number) => void;
    handlerOpenEditGroup?: (id: number) => void;
    changeFilterHandler: ({ fieldName, checkedValues }: TFilterFieldsType) => void;
    activeGroupTypeId: ActKc2GroupTypeEnum | undefined | null;
}) => {
    const variables = useMemo(() => ({ workOrderId }), [workOrderId]);

    const menuColumn = useMemo<any>(() => {
        if (activeGroupTypeId === ActKc2GroupTypeEnum.Custom) {
            return [
                {
                    cellClass,
                    headerName: '',
                    field: 'menu',
                    width: 50,
                    pinned: 'left',
                    lockPosition: 'left',
                    resizable: false,
                    cellRenderer: MenuCellRenderer,
                    cellRendererParams: {
                        handleDeleteGroup,
                        handlerOpenEditGroup,
                    },
                },
            ];
        }

        return [];
    }, [activeGroupTypeId, handleDeleteGroup, handlerOpenEditGroup]);

    return useMemo<
        (
            | ColDef<TGroupedData<ActKc2ListItemData, ActKc2GroupTypeEnum>>
            | ColGroupDef<TGroupedData<ActKc2ListItemData, ActKc2GroupTypeEnum>>
        )[]
    >(
        () => [
            ...menuColumn,
            {
                headerName: 'Номер акта',
                field: 'number',
                cellRenderer: GroupNameAndNumberRenderer,
                cellRendererParams: {
                    groupState,
                    changeGroupState,
                    isUseLink: true,
                },
                colSpan: getColSpan,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerActKC2GroupNumberFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Номер акта',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: string }) => `КС-2 ${value ? `№${value}` : ''}`,
                        },
                    ],
                    query: uerActKC2GroupNumberFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                flex: 1,
                minWidth: 200,
                cellClass,
            },
            {
                headerName: 'Дата акта',
                field: 'actDate',
                valueFormatter: dateFormatter('actDate'),
                filter: AgGridCustomFilterAdapter,
                width: 145,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    withSearch: false,
                    dataAccessor: 'uerActKC2GroupDateFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Дата акта',
                            field: 'value',
                            checkboxSelection: true,
                            valueFormatter: ({ value }: { value: string | null }) =>
                                value
                                    ? dateFormatter<FilterValueOfNullableOfDateTime>('value')({ data: { value } })
                                    : '<Без даты>',
                            width: 400,
                        },
                    ],
                    query: uerActKC2GroupDateFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                cellClass,
            },
            {
                headerName: 'Отчетный период',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Начало',
                        field: 'startDate',
                        valueFormatter: dateFormatter('startDate'),
                        width: 100,
                        cellClass,
                    },
                    {
                        headerName: 'Окончание',
                        field: 'finishDate',
                        valueFormatter: dateFormatter('finishDate'),
                        width: 120,
                        cellClass,
                    },
                ],
                cellClass,
            },
            {
                headerName: 'Группа затрат',
                field: 'costGroup',
                valueFormatter: costGroupFormatter,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    withSearch: false,
                    dataAccessor: 'uerActKC2GroupCostGroupFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Группа затрат',
                            field: 'value',
                            checkboxSelection: true,
                            valueFormatter: ({ value }: { value: CostGroupEnum }) =>
                                costGroupFormatter({ data: { costGroup: value } }),
                            width: 400,
                        },
                    ],
                    query: uerActKC2GroupCostGroupFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                width: 180,
                cellClass,
            },
            {
                headerName: 'Вид стр-ва',
                field: 'constructionType.name',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerActKC2GroupConstructionTypeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Вид стр-ва',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: uerActKC2GroupConstructionTypeFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                flex: 1,
                minWidth: 200,
                cellClass,
            },
            {
                headerName: 'Объект',
                field: 'workOrderObjectName',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerActKC2GroupObjectFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Объект',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: uerActKC2GroupObjectFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                flex: 1,
                minWidth: 200,
                cellClass,
            },
            {
                headerName: 'Стоимость, руб. без НДС',
                field: 'costWithoutTax',
                pinned: 'right',
                lockPosition: 'right',
                valueFormatter: costCellFormatter,
                cellStyle: {
                    justifyContent: 'flex-end',
                },
                sortable: true,
                comparator: fakeComparator,
                cellClass,
            },
        ],
        [changeFilterHandler, changeGroupState, groupState, menuColumn, variables],
    );
};
